import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { createInspection } from '../../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function AddInspectionModal({ open, onClose, onSuccess }) {
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    lease_id: '',
    inspection_type: 'routine',
    scheduled_date: ''
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [propsRes, leasesRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leases`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProperties(propsRes.data);
      setLeases(leasesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await createInspection({
        ...formData,
        scheduled_date: new Date(formData.scheduled_date).toISOString()
      });
      
      toast.success('Inspection scheduled successfully!');
      onSuccess();
      onClose();
      
      setFormData({
        property_id: '',
        lease_id: '',
        inspection_type: 'routine',
        scheduled_date: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule inspection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Schedule Inspection</DialogTitle>
          <DialogDescription>Schedule a property inspection</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData({ ...formData, property_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.address}, {property.suburb}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Lease (Optional)</Label>
            <Select
              value={formData.lease_id}
              onValueChange={(value) => setFormData({ ...formData, lease_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select lease (optional)" />
              </SelectTrigger>
              <SelectContent>
                {leases.map((lease) => (
                  <SelectItem key={lease.id} value={lease.id}>
                    Lease {lease.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Inspection Type *</Label>
            <Select
              value={formData.inspection_type}
              onValueChange={(value) => setFormData({ ...formData, inspection_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="entry">Entry</SelectItem>
                <SelectItem value="exit">Exit</SelectItem>
                <SelectItem value="final_bond">Final Bond</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Scheduled Date & Time *</Label>
            <Input
              type="datetime-local"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Scheduling...' : 'Schedule Inspection'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
